import { useMemo, useState } from 'react';
import { Minus } from 'lucide-react';
import { usePriceSeriesPoints } from '@/hooks/use-product-intelligence';
import {
  INITIAL_WINDOW,
  PRICE_WINDOWS,
  buildPriceSeries,
  pointDeltaFraction,
  priceInsights,
  rankInsights,
  type PricePoint,
  type PriceSeriesKind,
  type PriceWindow,
  type StockPosition,
  type VendorSeries,
} from '@/lib/product-intelligence';
import { formatCurrency, formatShortDate } from '@/lib/utils';
import {
  ColorDot,
  DeltaLabel,
  InsightList,
  IntelCard,
  IntelEmpty,
  IntelError,
  SectionLabel,
  SectionSkeleton,
  Segmented,
} from './intelligence-ui';
import { VendorTrendChart } from './vendor-trend-chart';

const KIND_OPTIONS = [
  { value: 'quoted' as const, label: 'Quoted price' },
  { value: 'paid' as const, label: 'Paid price' },
];

const WINDOW_OPTIONS = PRICE_WINDOWS.map((w) => ({ value: w.key, label: w.label }));

interface Props {
  productId: string;
  position: StockPosition;
  /** The product's linked vendor, for the "steady at" line. */
  primaryVendorName: string | null;
}

/**
 * Vendor price trends over 30 days, 3, 6 and 12 months, for either series:
 * what vendors QUOTE (vendor price history) or what was actually PAID
 * (purchase-order lines). One year is fetched once per series and the
 * shorter windows are derived in memory, so the window selector never spins.
 */
export function PricingPanel({ productId, position, primaryVendorName }: Props) {
  const [windowKey, setWindowKey] = useState<PriceWindow['key']>(INITIAL_WINDOW.key);
  const [kind, setKind] = useState<PriceSeriesKind>('quoted');
  // Pinned once so the window filter, the chart's right edge and the insight
  // text all agree on when "now" is.
  const [now] = useState(() => new Date());
  const priceWindow = PRICE_WINDOWS.find((w) => w.key === windowKey) ?? INITIAL_WINDOW;
  const isPaid = kind === 'paid';

  const { points, isLoading, isError, error, refetch } = usePriceSeriesPoints(productId, kind);
  const series = useMemo(() => buildPriceSeries(points, priceWindow, now), [points, priceWindow, now]);
  const insights = useMemo(() => rankInsights(priceInsights(series), 2), [series]);

  let body;
  if (isLoading) {
    body = (
      <div className="space-y-3">
        <SectionSkeleton lines={2} />
        <SectionSkeleton lines={4} />
      </div>
    );
  } else if (isError) {
    const message = (error as any)?.error?.message;
    body = (
      <IntelError
        message={`Could not load ${isPaid ? 'paid prices' : 'price history'}.${message ? ` ${message}` : ''}`}
        onRetry={() => refetch()}
      />
    );
  } else if (series.vendors.length === 0) {
    // An empty window is usually a real answer: the price simply held. Paid
    // has no such reading — no orders means nothing was bought, so it must
    // not borrow the product's cost and imply a purchase that never happened.
    const detail = isPaid
      ? 'No purchase orders were placed for this product in that time.'
      : position.cost !== null
        ? `Steady at ${formatCurrency(position.cost)}${primaryVendorName ? ` from ${primaryVendorName}` : ''}.`
        : 'No vendor has changed their price for this product in that time.';
    body = (
      <IntelEmpty
        icon={Minus}
        title={
          isPaid ? `No purchases in the ${priceWindow.fullName}` : `No price changes in the ${priceWindow.fullName}`
        }
        detail={detail}
      />
    );
  } else {
    const cheapest = series.vendors[0];
    const colorIndex = new Map(series.vendors.map((v, i) => [v.payeeId, i]));
    body = (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <IntelCard className="lg:col-span-3">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="text-3xl font-semibold tabular-nums text-foreground">
                {formatCurrency(cheapest.currentPrice)}
              </span>
              <DeltaLabel fraction={cheapest.windowDeltaFraction} className="text-sm" />
              <span className="min-w-0 truncate text-sm text-muted-foreground">
                {isPaid ? 'lowest' : 'best'} · {cheapest.payeeName}
              </span>
            </div>
            <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              {isPaid ? 'Paid per unit' : 'Cost per unit'} · {priceWindow.fullName}
            </div>
            <div className="mt-4">
              <VendorTrendChart series={series} now={now} />
            </div>
          </IntelCard>

          <div className="space-y-4 lg:col-span-2">
            <IntelCard className="p-0">
              <div className="px-4 pt-3">
                <SectionLabel>Vendors · cheapest first</SectionLabel>
              </div>
              <ul className="divide-y divide-border">
                {series.vendors.map((v, i) => (
                  <VendorLegendRow key={v.payeeId} series={v} index={i} isBest={i === 0 && series.vendors.length > 1} />
                ))}
              </ul>
            </IntelCard>
            <InsightList insights={insights} />
          </div>
        </div>

        <div>
          <SectionLabel>
            {isPaid ? 'Purchase log' : 'Change log'} · {priceWindow.fullName}
          </SectionLabel>
          <PriceChangeLog
            points={series.allPointsDescending}
            colorIndexFor={(id) => colorIndex.get(id) ?? 0}
            isPaid={isPaid}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented label="Time window" options={WINDOW_OPTIONS} value={windowKey} onChange={setWindowKey} />
        <Segmented label="Price series" options={KIND_OPTIONS} value={kind} onChange={setKind} />
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">
        {isPaid
          ? 'Paid price is what each purchase order actually cost per unit (drafts and cancelled orders excluded).'
          : 'Quoted price is what each vendor lists for this product. Switch to Paid price to see what orders actually cost.'}
      </p>
      {body}
    </div>
  );
}

function VendorLegendRow({ series, index, isBest }: { series: VendorSeries; index: number; isBest: boolean }) {
  return (
    <li className="flex items-center gap-2.5 px-4 py-2.5">
      <ColorDot index={index} />
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate text-sm font-medium text-foreground">{series.payeeName}</span>
        {isBest && (
          <span className="shrink-0 rounded bg-green-50 px-1.5 py-px text-[10px] font-bold tracking-wide text-green-700">
            BEST
          </span>
        )}
      </span>
      <span className="text-sm font-semibold tabular-nums text-foreground">{formatCurrency(series.currentPrice)}</span>
      <span className="w-14 text-right">
        <DeltaLabel fraction={series.windowDeltaFraction} />
      </span>
    </li>
  );
}

/**
 * The log under the chart. Quoted rows surface `vendor_price_history.reason`
 * ("annual increase", "volume discount"): the chart says what happened, this
 * says why. Paid rows name the purchase order instead.
 */
function PriceChangeLog({
  points,
  colorIndexFor,
  isPaid,
}: {
  points: PricePoint[];
  colorIndexFor: (payeeId: string) => number;
  isPaid: boolean;
}) {
  return (
    <IntelCard className="p-0">
      <ul className="divide-y divide-border">
        {points.map((p, i) => {
          const subtitle = [
            formatShortDate(p.changedAt.toISOString()),
            isPaid ? (p.orderNumber ? `PO ${p.orderNumber}` : null) : p.reason,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <li key={`${p.payeeId}-${p.changedAt.getTime()}-${i}`} className="flex items-start gap-2.5 px-4 py-2.5">
              <ColorDot index={colorIndexFor(p.payeeId)} className="mt-1.5" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{p.payeeName}</div>
                <div className="truncate text-xs text-muted-foreground">{subtitle}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-sm tabular-nums text-slate-700">
                  {p.oldPrice === null
                    ? formatCurrency(p.newPrice)
                    : `${formatCurrency(p.oldPrice)} → ${formatCurrency(p.newPrice)}`}
                </div>
                <DeltaLabel fraction={pointDeltaFraction(p)} />
              </div>
            </li>
          );
        })}
      </ul>
    </IntelCard>
  );
}
